import LiveReportingHealth from './LiveReportingHealth';
import DailyOperationalAudit from './DailyOperationalAudit';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { AlertCircle, AlertTriangle, CheckCircle, Shield, Globe, ExternalLink } from 'react-feather';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  ListGroup,
  ListGroupItem,
  Row
} from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import i18n from '../../../../i18n';

const defaultReport = {
  status: 'unknown',
  date: '',
  summary: '',
  warnings: [],
  critical_issues: [],
  things_that_look_healthy: [],
  prioritized_security_report: [],
  recommended_next_actions: [],
  documentation_update_plan: [],
  website_audit_addon: {
    title: '',
    overall_status: 'unknown',
    summary: '',
    scope: [],
    findings: [],
    route_checks: [],
    limitations: []
  },
  metrics: {
    critical_issue_count: 0,
    warning_count: 0,
    healthy_count: 0,
    security_finding_count: 0,
    documentation_update_count: 0,
    recommended_next_action_count: 0
  },
  links: {}
};

const statusTone = {
  critical: 'danger',
  warning: 'warning',
  healthy: 'success',
  unknown: 'secondary'
};

const DailyHealthReport = () => {
  const [report, setReport] = useState(defaultReport);
  const [loadError, setLoadError] = useState('');
  const reportTitle = 'Audit quotidien et santé des données';
  const websiteAudit = report.website_audit_addon || defaultReport.website_audit_addon;

  useEffect(() => {
    let cancelled = false;

    fetch(`${process.env.PUBLIC_URL || ''}/reports/daily/latest.json`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (!cancelled) {
          setReport({ ...defaultReport, ...data });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setLoadError(`Impossible de charger le rapport hebdomadaire (${error.message}).`);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const overviewChart = useMemo(() => ({
    series: [
      report.metrics.critical_issue_count || 0,
      report.metrics.warning_count || 0,
      report.metrics.healthy_count || 0
    ],
    options: {
      chart: {
        type: 'donut',
        toolbar: { show: false }
      },
      labels: [
        i18n.t('Dashboard_Report_Critical'),
        i18n.t('Dashboard_Report_Warnings'),
        i18n.t('Dashboard_Report_HealthyItems')
      ],
      colors: ['#d7263d', '#f4af17', '#1f9d55'],
      legend: {
        position: 'bottom'
      },
      dataLabels: {
        enabled: true
      },
      stroke: {
        colors: ['#ffffff']
      }
    }
  }), [report.metrics]);

  const actionChart = useMemo(() => ({
    series: [
      {
        name: 'Actions',
        data: [
          report.metrics.security_finding_count || 0,
          report.metrics.documentation_update_count || 0,
          report.metrics.recommended_next_action_count || 0
        ]
      }
    ],
    options: {
      chart: {
        type: 'bar',
        toolbar: { show: false }
      },
      colors: ['#16324f'],
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: '50%'
        }
      },
      xaxis: {
        categories: ['Sécurité', 'Documentation', 'Suites']
      },
      yaxis: {
        min: 0,
        forceNiceScale: true
      },
      grid: {
        borderColor: '#e7ebf3'
      },
      dataLabels: {
        enabled: true
      }
    }
  }), [report.metrics]);

  return (
    <Fragment>
      <Breadcrumbs mainTitle={reportTitle} parent={i18n.t('Dashboard_Sidebar_Reports')} title={i18n.t('Dashboard_Report_DailyHealthAudit')} />
      <Container fluid className="default-dashboard">
        <DailyOperationalAudit />
        <LiveReportingHealth />
        <details className="mb-4"><summary className="mb-3">Consulter l’audit manuel archivé du {report.date || 'rapport publié'}</summary>
        <Alert color="warning">Archive : l’audit ci-dessous date du {report.date || 'rapport publié'}. Ses constats ne décrivent pas nécessairement la situation actuelle.</Alert>
        {loadError ? (
          <Alert color="danger">{loadError}</Alert>
        ) : null}

        <Row>
          <Col xl="8">
            <Card className="shadow-sm">
              <CardHeader className="pb-0 d-flex justify-content-between align-items-start">
                <div>
                  <h4 className="mb-1">{i18n.t('Dashboard_Report_GlobalStatus')}</h4>
                  <p className="text-muted mb-0">{i18n.t('Dashboard_Report_ReportOf')} {report.date || 'N/A'}</p>
                </div>
                <Badge color={statusTone[report.status] || 'secondary'} pill>
                  {String(report.status || 'unknown').toUpperCase()}
                </Badge>
              </CardHeader>
              <CardBody>
                <p className="mb-3">{report.summary || i18n.t('Dashboard_Report_NoSummary')}</p>
                <Row>
                  <Col md="4" sm="6">
                    <Card className="border border-danger h-100">
                      <CardBody>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <AlertCircle size={18} className="text-danger" />
                          <span className="fw-bold">{i18n.t('Dashboard_Report_Critical')}</span>
                        </div>
                        <h2 className="mb-1">{report.metrics.critical_issue_count || 0}</h2>
                        <small className="text-muted">{i18n.t('Dashboard_Report_CriticalSubtitle')}</small>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="4" sm="6">
                    <Card className="border border-warning h-100">
                      <CardBody>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <AlertTriangle size={18} className="text-warning" />
                          <span className="fw-bold">{i18n.t('Dashboard_Report_Warnings')}</span>
                        </div>
                        <h2 className="mb-1">{report.metrics.warning_count || 0}</h2>
                        <small className="text-muted">{i18n.t('Dashboard_Report_WarningsSubtitle')}</small>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="4" sm="12">
                    <Card className="border border-success h-100">
                      <CardBody>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <CheckCircle size={18} className="text-success" />
                          <span className="fw-bold">{i18n.t('Dashboard_Report_HealthyItems')}</span>
                        </div>
                        <h2 className="mb-1">{report.metrics.healthy_count || 0}</h2>
                        <small className="text-muted">{i18n.t('Dashboard_Report_HealthySubtitle')}</small>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="shadow-sm h-100">
              <CardHeader className="pb-0">
                <h4 className="mb-1">{i18n.t('Dashboard_Report_QuickLinks')}</h4>
                <p className="text-muted mb-0">{i18n.t('Dashboard_Report_QuickLinksSubtitle')}</p>
              </CardHeader>
              <CardBody>
                <ListGroup flush>
                  {Object.entries(report.links || {}).map(([key, value]) => (
                    <ListGroupItem key={key} className="px-0 d-flex justify-content-between align-items-center">
                      <span className="text-capitalize">{key.replace(/_/g, ' ')}</span>
                      <a href={value} target="_blank" rel="noreferrer" className="text-primary">
                        {i18n.t('Dashboard_Report_Open')} <ExternalLink size={14} />
                      </a>
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xl="12">
            <Card className="shadow-sm">
              <CardHeader className="pb-0 d-flex justify-content-between align-items-start">
                <div>
                  <h4 className="mb-1">{websiteAudit.title || 'Audit complet du site web'}</h4>
                  <p className="text-muted mb-0">
                    Vérification de la surface publique, des routes, du déploiement et des signaux d&apos;exécution.
                  </p>
                </div>
                <Badge color={statusTone[websiteAudit.overall_status] || 'secondary'} pill>
                  {String(websiteAudit.overall_status || 'unknown').toUpperCase()}
                </Badge>
              </CardHeader>
              <CardBody>
                <p className="mb-3">{websiteAudit.summary || 'Aucun audit web détaillé n’est disponible pour ce rapport.'}</p>
                <Row>
                  <Col xl="4" md="6">
                    <h6 className="mb-2">Périmètre</h6>
                    <ListGroup flush>
                      {(websiteAudit.scope || []).map((item, index) => (
                        <ListGroupItem key={`${item}-${index}`} className="px-0">
                          <CheckCircle size={15} className="text-success me-2" />
                          {item}
                        </ListGroupItem>
                      ))}
                    </ListGroup>
                  </Col>
                  <Col xl="4" md="6">
                    <h6 className="mb-2">Constats</h6>
                    <ListGroup flush>
                      {(websiteAudit.findings || []).map((item, index) => (
                        <ListGroupItem key={`${item.title}-${index}`} className="px-0">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <strong>{item.title}</strong>
                            <Badge color={statusTone[item.severity] || 'secondary'} pill>
                              {String(item.severity || '').toUpperCase()}
                            </Badge>
                          </div>
                          <div className="text-muted small mb-1">{item.evidence}</div>
                          <div className="small">{item.impact}</div>
                        </ListGroupItem>
                      ))}
                    </ListGroup>
                  </Col>
                  <Col xl="4" md="12">
                    <h6 className="mb-2">Limites de vérification</h6>
                    <ListGroup flush>
                      {(websiteAudit.limitations || []).map((item, index) => (
                        <ListGroupItem key={`${item}-${index}`} className="px-0">
                          <AlertTriangle size={15} className="text-warning me-2" />
                          {item}
                        </ListGroupItem>
                      ))}
                    </ListGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xl="12">
            <Card className="shadow-sm">
              <CardHeader className="pb-0">
                <h4 className="mb-1">Routes et surfaces vérifiées</h4>
              </CardHeader>
              <CardBody>
                <ListGroup flush>
                  {(websiteAudit.route_checks || []).map((item, index) => (
                    <ListGroupItem key={`${item.path}-${index}`} className="px-0">
                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <strong>{item.path}</strong>
                        <Badge color={statusTone[item.status] || 'secondary'} pill>
                          {String(item.status || '').toUpperCase()}
                        </Badge>
                      </div>
                      <div className="small mb-1">{item.observation}</div>
                      <div className="text-muted small">{item.evidence}</div>
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xl="5">
            <Card className="shadow-sm">
              <CardHeader className="pb-0">
                <h4 className="mb-1">{i18n.t('Dashboard_Report_FindingsBreakdown')}</h4>
                <p className="text-muted mb-0">{i18n.t('Dashboard_Report_FindingsBreakdownSubtitle')}</p>
              </CardHeader>
              <CardBody>
                <Chart options={overviewChart.options} series={overviewChart.series} type="donut" height={320} />
              </CardBody>
            </Card>
          </Col>
          <Col xl="7">
            <Card className="shadow-sm">
              <CardHeader className="pb-0">
                <h4 className="mb-1">{i18n.t('Dashboard_Report_RemainingWork')}</h4>
                <p className="text-muted mb-0">{i18n.t('Dashboard_Report_RemainingWorkSubtitle')}</p>
              </CardHeader>
              <CardBody>
                <Chart options={actionChart.options} series={actionChart.series} type="bar" height={320} />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xl="6">
            <Card className="shadow-sm">
              <CardHeader className="pb-0">
                <h4 className="mb-1">{i18n.t('Dashboard_Report_CriticalIssues')}</h4>
              </CardHeader>
              <CardBody>
                <ListGroup flush>
                  {(report.critical_issues || []).map((item, index) => (
                    <ListGroupItem key={`${item.title}-${index}`} className="px-0">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <Globe size={16} className="text-danger" />
                        <strong>{item.title}</strong>
                      </div>
                      <div className="text-muted small mb-1">{item.evidence}</div>
                      <div className="small"><strong>{i18n.t('Dashboard_Report_Risk')}:</strong> {item.risk}</div>
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </CardBody>
            </Card>
          </Col>

          <Col xl="6">
            <Card className="shadow-sm">
              <CardHeader className="pb-0">
                <h4 className="mb-1">{i18n.t('Dashboard_Report_SecurityPriorities')}</h4>
              </CardHeader>
              <CardBody>
                <ListGroup flush>
                  {(report.prioritized_security_report || []).map((item, index) => (
                    <ListGroupItem key={`${item.title}-${index}`} className="px-0">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <Shield size={16} className="text-danger" />
                        <strong>{item.title}</strong>
                        <Badge color={statusTone[item.severity] || 'secondary'} pill>
                          {String(item.severity || '').toUpperCase()}
                        </Badge>
                      </div>
                      <div className="text-muted small mb-1">{item.evidence}</div>
                      <div className="small mb-1"><strong>{i18n.t('Dashboard_Report_Risk')}:</strong> {item.risk}</div>
                      <div className="small"><strong>{i18n.t('Dashboard_Report_Fix')}:</strong> {item.recommended_fix}</div>
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xl="6">
            <Card className="shadow-sm">
              <CardHeader className="pb-0">
                <h4 className="mb-1">{i18n.t('Dashboard_Report_HealthyItems')}</h4>
              </CardHeader>
              <CardBody>
                <ListGroup flush>
                  {(report.things_that_look_healthy || []).map((item, index) => (
                    <ListGroupItem key={`${item}-${index}`} className="px-0">
                      <CheckCircle size={15} className="text-success me-2" />
                      {item}
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </CardBody>
            </Card>
          </Col>

          <Col xl="6">
            <Card className="shadow-sm">
              <CardHeader className="pb-0">
                <h4 className="mb-1">{i18n.t('Dashboard_Report_RecommendedActions')}</h4>
              </CardHeader>
              <CardBody>
                <ListGroup flush>
                  {(report.recommended_next_actions || []).map((item, index) => (
                    <ListGroupItem key={`${item}-${index}`} className="px-0">
                      <AlertTriangle size={15} className="text-warning me-2" />
                      {item}
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </CardBody>
            </Card>
          </Col>
        </Row>
        </details>
      </Container>
    </Fragment>
  );
};

export default DailyHealthReport;
